// 2) Third-party library imports
import { get, map, filter as lFilter } from 'lodash-es';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

// 3a) Local imports � Constants
import { API_NAME } from '../../config/config';
import { DETAILS_TAB, REMITTANCE_ADVICES_PAGE } from '../../config/tableAndPageConstants';
import { FamilyNameAttribute, GivenNameAttribute } from '../../constants/authUserAttributes';

// 3c) Local imports � Project-specific modules / components / types
import { _queries as queries } from '../../graphql/queries.graphql';
import { appliedFilterIndicator } from '../../constants/filterbar';
import { PageData } from '../common/types';
import { Payment } from '../payments/types';
import { Invoice } from '../invoices/types';
import { SalesOrder } from '../salesOrders/types';
import { Credit } from '../credits/types';
import {
    RemittanceAdvice,
    RemittanceAdvicesActionTypes
} from './types';

// 3d) Local imports � Utility functions / interfaces
import {
    checkIfEmailIsValid,
    formatDateToDateObjectUTC,
    getRegionallyApiResponse,
    getRegionallyGraphqlResponse,
    getSortFieldsWithCustomFields,
    populateSortFieldsParamSortParams,
    removeAppliedFiltersForApiRequest,
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import dayjs from 'src/config/dayjs';
import client, { post } from 'src/api';

// 3e) Local imports � Redux actions & selectors
import { getCurrentUser } from '../auth/authSlice';
import { getCloudImportType } from '../companies/companySlice';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';
import {
    getRemittanceAdviceDetailsError,
    getRemittanceAdviceDetailsSuccess,
    getRemittanceAdvicesSuccessAction,
    setRemittanceAdviceSelectedIdSuccessAction,
    getRemittanceAdviceInvoicesErrorAction,
    getRemittanceAdviceInvoicesSuccessAction,
    getRemittanceAdviceSalesOrdersErrorAction,
    getRemittanceAdviceSalesOrdersSuccessAction,
    getRemittanceAdviceConversationSuccessAction,
    getRemittanceAdviceConversationErrorAction,
    getRemittanceAdviceChangesSuccessAction,
    getRemittanceAdviceChangesErrorAction,
    getRemittanceAdvicePaymentsErrorAction,
    getRemittanceAdvicePaymentsSuccessAction,
    getRemittanceAdviceCreditsSuccessAction,
    getRemittanceAdviceCreditsErrorAction,
    getRemittanceAdviceCustomersSuccessAction,
    getRemittanceAdviceCustomersErrorAction,
    reviewRemittanceAdviceSuccess,
    getRAProcessingConfigurationSuccessAction,
    getRAProcessingConfigurationErrorAction,
    saveRAProcessingConfigurationSuccessAction,
    saveRAProcessingConfigurationErrorAction,
    saveRARebateMappingConfigurationResponseAction,
    getRemittanceAdviceRebatesSuccessAction,
    getRemittanceAdviceRebatesErrorAction,
    getRemittanceAdviceSummarySuccess,
    getRemittanceAdviceSummaryError,
    getConfirmRemittanceAdviceInvoicesSuccessAction,
    getConfirmRemittanceAdviceCreditsSuccessAction,
    getConfirmRemittanceAdviceRebatesSuccessAction,
    saveRemittanceAdviceRebate,
    saveRemittanceAdviceCredit,
    saveRemittanceAdviceInvoice,
    saveRemittanceAdvice,
    getRARebateMappingsRequestAction,
    saveRARebateMappingConfigurationRequestAction,
    saveRAProcessingConfigurationRequestAction,
    reviewOrganisationRemittanceAdviceRequest,
    reviewRemittanceAdviceRequest,
    getRAProcessingConfigurationRequestAction,
    getRACustomFieldsForCompanyRequestAction,
    getRACustomFieldsForOrganisationRequestAction,
    getRemittanceAdvicePaymentsRequestAction,
    getRemittanceAdviceChangesRequestAction,
    getRemittanceAdviceConversationRequestAction,
    getRemittanceAdviceRebatesRequestAction,
    getRemittanceAdviceCreditsRequestAction,
    getRemittanceAdviceInvoicesRequestAction,
    getRemittanceAdviceCustomersRequestAction,
    setRemittanceAdviceSelectedIdRequestAction,
    organisationConfirmRemittanceAdviceDisregardTasksRequest,
    confirmRemittanceAdviceDisregardTasksRequest,
    confirmRemittanceAdviceRequest,
    getRemittanceAdviceSummary,
    getRemittanceAdviceDetails,
    getRemittanceAdvicesRequestAction,
    remittanceAdviceAddCommentRequestAction,
    getRemittanceAdvicesPageData,
    getRAImportStatusRequestAction,
    createAdjustmentsForInvoicesRequest,
    getRemittanceAdviceSalesOrdersRequestAction,
    getRemittanceAdviceProcessorReportRequest,
    getRemittanceAdviceProcessorReportSuccessAction,
    getRemittanceAdviceProcessorReportErrorAction,
} from './remittanceAdviceSlice';

const preparePayloadForTaskFilter = ({ payload, canExcludeTasks, parseCustomFields }: { 
    payload: any, 
    canExcludeTasks: boolean,
    parseCustomFields: boolean
 }) => {
    const {
        filter,
        filters,
        sortBy,
        sortAscending,
        usingMultipleWorkflow,
        isPaymentPlanEnabled,
        taskIds,
        excludeTasks,
        recordLimit
    } = payload;

    const finalPayload: any = {
        Ascending: sortAscending,
        ...(
            canExcludeTasks ? {
                TaskIds: taskIds,
                ExcludeTasks: excludeTasks,
                RecordLimit: recordLimit
            } : {}
        )
    };
    const finalFilter = filter || filters; // Handle different filter property names in models

    if (finalFilter) {
        const typeValue = isPaymentPlanEnabled
            ? finalFilter.Type
            : lFilter(finalFilter.Type, (ft: any) => ft !== 8);
        const usedFilters = {
            ...finalFilter,
            Type: typeValue,
            [`Type${appliedFilterIndicator}`]: typeValue,
        };
        const cleanFilters = removeAppliedFiltersForApiRequest(
            usedFilters,
            true,
            'task'
        );
        const assignedUserValue = cleanFilters.AssignedUserId;

        if (assignedUserValue) {
            if (checkIfEmailIsValid(assignedUserValue)) {
                delete cleanFilters.AssignedUserId;
                cleanFilters.AssignedEmailAddress = assignedUserValue;
            }
        }

        if (parseCustomFields && cleanFilters.CustomFieldFilters) {
            cleanFilters.CustomFieldFilters = JSON.parse(
                cleanFilters.CustomFieldFilters
            );
        }

        if (!usingMultipleWorkflow) {
            delete cleanFilters.WorkflowIds;
        }

        Object.assign(finalPayload, cleanFilters);
    }

    if (sortBy) {
        const sortFields = populateSortFieldsParamSortParams(sortBy);

        if (parseCustomFields && sortFields.CustomFieldSort) {
            sortFields.CustomFieldSort = JSON.parse(
                sortFields.CustomFieldSort
            );
        }

        Object.assign(finalPayload, sortFields);
    }

    return finalPayload;
}

 /**
 * Function calling the API for fetching the remittance advice data based on the given id.
 * @param param0
 */
function* handleGetRemittanceAdviceRequest({ payload }: any) {
    const errorMessage = "Error fetching remittance advice details. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            sortBy,
            sortAscending,
            ActiveState,
            pageSize,
            currentPage,
            CompanyId, 
            CustomerId,
            PaidDateMin, 
            PaidDateMax, 
            AmountType, 
            PaidAmountMin, 
            PaidAmountMax, 
            SortField,
            callback 
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true, 'remittanceAdvices');        
        const sortObject = getSortFieldsWithCustomFields(SortField);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REMITTANCE_ADVICES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    ...sortObject,
                    Ascending: sortAscending,
                    ActiveState: ActiveState,
                    PageSize: pageSize,
                    Skip: currentPage * REMITTANCE_ADVICES_PAGE.pageSize,
                    CompanyId: CompanyId,
                    CustomerId: CustomerId,
                    PaidDateMin: isNaN(PaidDateMin) ? cleanFilters.PaidDateMin : PaidDateMin,
                    PaidDateMax: isNaN(PaidDateMax) ? cleanFilters.PaidDateMax : PaidDateMax,
                    PaidAmountMin: PaidAmountMin,
                    PaidAmountMax: PaidAmountMax,
                    AmountType: AmountType ? AmountType: cleanFilters.AmountType,
                    //SortField: SortField,
                    callback
                }
            }
        );

        const RemittanceAdvice = get(res.data, 'GetRemittanceAdvicesForCompany.RemittanceAdvices');

        if (RemittanceAdvice) {
            const responsePayload = {
                data: RemittanceAdvice,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(RemittanceAdvice.length < pageSize) &&
                        !(pageSize < REMITTANCE_ADVICES_PAGE.pageSize),
                },
            };

            yield put(getRemittanceAdvicesSuccessAction(responsePayload));
        } else {
            yield put(getRemittanceAdviceDetailsError([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceDetailsError([errorMessage]));
    }
}   

/**
 * Function calling the API for fetching the remittance advice data based on the given id.
 * @param param0
 */
function* handleGetRemittanceAdviceDetailsRequest({
    payload: remittanceAdviceId,
}: any) {
    const errorMessage = "Error fetching remittance advice details. Please try again later.";

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REMITTANCE_ADVICE_DETAILS,
                variables: {
                    RemittanceAdviceId: remittanceAdviceId
                }
            }
        );

        const RemittanceAdvice = get(res.data, 'GetRemittanceAdviceDetailsForCompany');

        const paymentId = get(RemittanceAdvice, 'PaymentResource.Id');
        
        let Payment: any;

        if (paymentId) {
            const resPayment: DynamicObject = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_PAYMENT_DETAILS_FOR_COMPANY, 
                    variables: {
                        PaymentId: paymentId
                    }
                }
            );
            
            Payment = get(resPayment.data, 'GetPaymentDetailsForCompany');
        }

        if (RemittanceAdvice) {
            const responsePayload = {
                record: RemittanceAdvice,
                payment: Payment
            };

            yield put(getRemittanceAdviceDetailsSuccess(responsePayload));
        } else {
            yield put(getRemittanceAdviceDetailsError([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceSummaryError([errorMessage]));
    }
}

/**
 * Function calling the API for fetching the remittance advice data based on the given id.
 * @param param0
 */
function* handleGetRemittanceAdviceSummaryRequest({
    payload: { remittanceAdviceId, companyId },
}: any) {
    const errorMessage = "Error fetching remittance advice details. Please try again later.";

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_REMITTANCE_ADVICE_SUMMARY,
            {
                RemittanceAdviceId: remittanceAdviceId,
                CompanyId: companyId
            }
        );

        const RemittanceAdvice = get(res.data, 'GetRemittanceAdviceSummary');
        const paymentId = get(RemittanceAdvice, 'PaymentResource.Id');

        let Payment: any;

        if (paymentId) {
            const resPayment: DynamicObject = yield call(
                getRegionallyGraphqlResponse,
                apiUrl,
                queries.GET_PAYMENT_DETAILS_FOR_COMPANY,
                {
                    PaymentId: paymentId,
                    CompanyId: companyId
                }
            );
            
            Payment = get(resPayment.data, 'GetPaymentDetailsForCompany');
        }

        if (RemittanceAdvice) {
            const responsePayload = {
                record: RemittanceAdvice,
                payment: Payment
            };

            yield put(getRemittanceAdviceSummarySuccess(responsePayload));
        } else {
            yield put(getRemittanceAdviceSummaryError([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceSummaryError([errorMessage]));
    }
}

/**
 * Function called when confirming a remittance advice.
 * @param param0
 */
function* handleConfirmRemittanceAdviceRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        yield call(post,
            {
                apiName: API_NAME,
                path: '/task/action/remittance-advice',
                options: {
                    body: payload,
                }
            }
        );

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called when confirming a remittance advice disregard tasks.
 * @param param0
 */
function* handleConfirmRemittanceAdviceDisregardTasksRequest({ payload: sagaPayload }: any) {
    const {
        CompanyId,
        callback,
    } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter, CompanyId },
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/disregard/remittanceadvice',
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleOrganisationConfirmRemittanceAdviceDisregardTasksRequest({ payload: sagaPayload }: any) {
    const {
        CompanyId,
        callback,
    } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter, CompanyId },
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/organisation/disregard/remittanceadvice',
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/* Function that sets the selected remittanceadviceId id for reference.
 * @param param0
 */
function* handleSetRemittanceAdviceSelectedIdRequest({ payload }: any) {
    const { remittanceAdviceId, callback } = payload;
    yield put(setRemittanceAdviceSelectedIdSuccessAction(remittanceAdviceId));

    if (callback) {
        callback();
    }
}

/**
 * Function for adding comment to the remittance advice's conversation list.
 * @param param0
 */
function* handleRemittanceAdviceAddCommentRequest({ payload: sagaPayload }: any) {
    const { filter, remittanceAdviceIds, excludeRemittanceAdvices, comment, callback } = sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(filter, true, 'remittanceAdvices');

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }
    
    const payload = {
        RemittanceAdviceManagementFilter: {
            ...cleanFilters,
            RemittanceAdviceIds: remittanceAdviceIds,
            ExcludeRemittanceAdvices: excludeRemittanceAdvices,
        },
        Comment: comment,
    };

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/conversation/save/remittanceadvicecomment',
            'POST',
            payload
        );

        if (callback) {
            let RefetchList = true;

            if (remittanceAdviceIds.length === 1 && excludeRemittanceAdvices === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const remittanceAdvicesUpdated: DynamicObject[] = map(
                    remittanceAdviceIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(getRemittanceAdvicesPageData);

                yield put(
                    getRemittanceAdvicesSuccessAction({
                        data: remittanceAdvicesUpdated as RemittanceAdvice[],
                        pageData,
                        mergeData: true
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that gets the Customers list for a certain remittance advice.
 * @param param0
 */
function* handleGetRemittanceAdviceCustomersRequest({ payload }: any) {
    const errorMessage = `Error fetching remittance advice's invoice list. Please try again later.`;

    try {
        const {
            filters,
            customerState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            RemittanceAdviceId,
            CompanyId
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_REMITTANCE_ADVICE_CUSTOMER,
            {
                ...cleanFilters,
                CustomerState: customerState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CUSTOMER_LIST_COMPLETE.pageSize,
                RemittanceAdviceId,
                CompanyId
            }
        );

        const { Customers } = get(res.data, 'GetCustomersForRemittanceAdvice');

        if (Customers) {
            const responsePayload = {
                data: Customers,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Customers.length < pageSize),
                },
            };

            yield put(getRemittanceAdviceCustomersSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceCustomersErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the invoices list for a certain remittance advice.
 * @param param0
 */
function* handleGetRemittanceAdviceInvoicesRequest({ payload }: any) {
    const errorMessage = `Error fetching remittance advice's invoice list. Please try again later.`;

    try {
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            type,
            companyId
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_REMITTANCE_ADVICE_INVOICE,
            {
                ...cleanFilters,
                CompanyId: companyId,
                InvoiceState: invoiceState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
            }
        );

        const { RemittanceAdviceInvoices } = get(res.data, 'GetRemittanceAdviceInvoicesForRemittanceAdvice');

        if (RemittanceAdviceInvoices) {
            if (type == 'Confirm RA') {
                const responsePayload = {
                    data: RemittanceAdviceInvoices,
                    pageData: {
                        pageSize: pageSize,
                        currentPage: currentPage,
                        hasNextPage: !(RemittanceAdviceInvoices.length < pageSize),
                    },
                };
                yield put(getConfirmRemittanceAdviceInvoicesSuccessAction(responsePayload));
            } else {
                const Invoices: Invoice[] = RemittanceAdviceInvoices.map((inv: any) => inv.Invoice);
                const responsePayload = {
                    data: Invoices,
                    pageData: {
                        pageSize: pageSize,
                        currentPage: currentPage,
                        hasNextPage: !(Invoices.length < pageSize),
                    },
                };

                yield put(getRemittanceAdviceInvoicesSuccessAction(responsePayload));
            }
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceInvoicesErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the invoices list for a certain remittance advice.
 * @param param0
 */
function* handleGetRemittanceAdviceSalesOrdersRequest({ payload }: any) {
    const errorMessage = `Error fetching remittance advice's sales order list. Please try again later.`;

    try {
        const {
            filters,
            salesOrderState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REMITTANCE_ADVICE_SALES_ORDERS,
                variables: {
                    ...cleanFilters,
                    InvoiceState: salesOrderState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.SALES_ORDER_LIST_COMPLETE.pageSize,
                }
            }
        );

        const { RemittanceAdviceSalesOrders } = get(res.data, 'GetRemittanceAdviceSalesOrdersForRemittanceAdvice');

        if (RemittanceAdviceSalesOrders) {
            const SalesOrders: SalesOrder[] = RemittanceAdviceSalesOrders
            .filter((sao: any) => sao.SalesOrder !== null)
            .map((sao: any) => sao.SalesOrder);
            
            const responsePayload = {
                data: SalesOrders,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(SalesOrders.length < pageSize),
                },
            };

            yield put(getRemittanceAdviceSalesOrdersSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceSalesOrdersErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the credits list for a certain customer.
 * @param param0
 */
function* handleGetRemittanceAdviceCreditsRequest({ payload }: any) {
    const errorMessage = `Error fetching customer's credit list. Please try again later.`;

    try {
        const {
            filters,
            creditState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            type,
            companyId
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'remittanceAdvices'
        );
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_REMITTANCE_ADVICE_CREDITS,
            {
                ...cleanFilters,
                CreditState: creditState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                CompanyId: companyId
            }
        );

        const { RemittanceAdviceCredits } = get(res.data, 'GetRemittanceAdviceCreditsForRemittanceAdvice');

        if (RemittanceAdviceCredits) {
            if (type == 'Confirm RA'){
                const responsePayload = {
                    data: RemittanceAdviceCredits,
                    pageData: {
                        pageSize: pageSize,
                        currentPage: currentPage,
                        hasNextPage: !(RemittanceAdviceCredits.length < pageSize),
                    },
                };

                yield put(getConfirmRemittanceAdviceCreditsSuccessAction(responsePayload));
            } else {
                const Credits: Credit[] = RemittanceAdviceCredits
                    .filter((cre: any) => cre.Credit !== null)
                    .map((cre: any) => cre.Credit);

                const responsePayload = {
                    data: Credits,
                    pageData: {
                        pageSize: pageSize,
                        currentPage: currentPage,
                        hasNextPage: !(Credits.length < pageSize),
                    },
                };
    
                yield put(getRemittanceAdviceCreditsSuccessAction(responsePayload));
            }            
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceCreditsErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the rebate list for a certain customer.
 * @param param0
 */
function* handleGetRemittanceAdviceRebatesRequest({ payload }: any) {
    const errorMessage = `Error fetching customer's credit list. Please try again later.`;

    try {
        const {
            filters,
            creditState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            type,
            companyId
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'remittanceAdvices'
        );

        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_REMITTANCE_ADVICE_REBATE,
            {
                ...cleanFilters,
                CreditState: creditState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                CompanyId: companyId
            }
        );

        const { RemittanceAdviceRebates } = get(res.data, 'GetRemittanceAdviceRebatesForRemittanceAdvice');

        if (RemittanceAdviceRebates) {
            if (type == 'Confirm RA') {
                const responsePayload = {
                    data: RemittanceAdviceRebates,
                    pageData: {
                        pageSize: pageSize,
                        currentPage: currentPage,
                        hasNextPage: !(RemittanceAdviceRebates.length < pageSize),
                    },
                };

                yield put(getConfirmRemittanceAdviceRebatesSuccessAction(responsePayload));
            } else {
                const Rebate: Credit[] = RemittanceAdviceRebates
                    .filter((cre: any) => cre.Credit !== null)
                    .map((reb: any) => reb.Rebate);
                
                const responsePayload = {
                    data: Rebate,
                    pageData: {
                        pageSize: pageSize,
                        currentPage: currentPage,
                        hasNextPage: !(Rebate.length < pageSize),
                    },
                };
    
                yield put(getRemittanceAdviceRebatesSuccessAction(responsePayload));
            }            
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceRebatesErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the conversation list of a given remittance advice.
 * @param param0
 */
function* handleGetRemittanceAdviceConversationRequest({ payload }: any) {
    const errorMessage = "Error fetching remittance advice's conversation list. Please try again later.";

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            undefined,
            true
        );

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CONVERSATION_LINES_FOR_COMPANY,
            {
                ...cleanFilters,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
            }
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationLinesForCompany'
        );

        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getRemittanceAdviceConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceConversationErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the remittanceAdviceChanges list of a given remittance advice.
 * @param param0
 */
function* handleGetRemittanceAdviceChangesRequest({ payload }: any) {
    const errorMessage = "Error fetching remittance advice changes list. Please try again later.";

    try {
        const { filters, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CHANGE_LINES_FOR_COMPANY,
            {
                ...cleanFilters,
                PageSize: pageSize,
                Skip:
                    currentPage * DETAILS_TAB.REMITTANCE_ADVICE_CHANGES_TIMELINE.pageSize,
            }
        );

        const { ChangeLines } = get(res.data, 'GetChangeLinesForCompany');

        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.REMITTANCE_ADVICE_CHANGES_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getRemittanceAdviceChangesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceChangesErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the payments list for a certain customer.
 * @param param0
 */
function* handleGetRemittanceAdvicePaymentsRequest({ payload }: any) {
    const errorMessage = `Error fetching customer's payment list. Please try again later.`;

    try {
        const {
            filters,
            paymentState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'remittanceAdvices'
        );
        const cloudImportType: string = yield select(getCloudImportType);
        
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENT_DETAILS_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    // PaymentState: paymentState,
                    // SortField: sortBy,
                    // Ascending: sortAscending,
                    // PageSize: pageSize,
                    // Skip: currentPage * DETAILS_TAB.PAYMENT_LIST_COMPLETE.pageSize,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            }
        );

        const Payments: Payment[] = [];
        Payments.push(get(res.data, 'GetPaymentDetailsForCompany'));
        
        if (Payments) {
            const responsePayload = {
                data: Payments,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Payments.length < pageSize),
                },
            };

            yield put(getRemittanceAdvicePaymentsSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdvicePaymentsErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the RA custom fields for a company.
 * @param param0
 */
function* handleGetRACustomFieldsForCompanyRequest({ payload }: any) {
    /*const errorMessage = '';*/
    const { 
        CompanyId,
        FieldName,
        callback
    } = payload;

    try {
        //const errorMessage =
        //'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_RA_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetRACustomFieldsForCompany');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;

            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the RA custom fields for a company.
 * @param param0
 */
function* handleGetRACustomFieldsForOrganisationRequest({ payload }: any) {
    /*const errorMessage = '';*/
    const { 
            CompanyIds, 
            FieldName,
            callback
        } = payload;
    try {
        //const errorMessage =
        //'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_RA_CUSTOM_FIELDS_FOR_ORGANISATION,
                variables: {
                    CompanyIds: CompanyIds,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetRACustomFieldsForOrganisation');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

 /** Function for reviewing RA.
 * @param param0
 */
function* handleReviewRemittanceAdviceRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME, 
            path: '/task/allocation-confirmation', 
            options: {
                body: payload,
            }
        });

        const Allocation: DynamicObject = yield res.body.json();

        if (Allocation) {
            yield put(
                reviewRemittanceAdviceSuccess({
                    AllocationErrors: Allocation.AllocationErrors,
                    AllocationItems: Allocation.AllocationItems,
                })
            );
        }
        
        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }       

    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleReviewOrganisationRemittanceAdviceRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME, 
            path: '/task/organisation/allocation-confirmation', 
            options: {
                body: payload,
            }
        });

        if (res) {
            yield put(
                reviewRemittanceAdviceSuccess({
                    AllocationErrors: res.AllocationErrors,
                    AllocationItems: res.AllocationItems,
                })
            );
        }
        
        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for getting the RAProcessingConfiguration.
 */
function* handleGetRAProcessingConfigurationRequest() {
    const errorMessage = `Error fetching remittance advice processing configuration details. Please try again later.`;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_RA_PROCESSING_CONFIG
            }
        );

        const raProcessingConfigurationData = get(
            res.data,
            'GetRAProcessingConfig'
        );

        yield put(getRAProcessingConfigurationSuccessAction(raProcessingConfigurationData));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
        yield put(getRAProcessingConfigurationErrorAction([errorMessage]));
    }
}

/**
 * Function for saving RA Customization settings.
 * @param param0
 */
function* handleSaveRAProcessingConfigurationRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/remittanceadvice/raprocessor/save',
            options: {
                body: payload
            }
        });
        yield put(saveRAProcessingConfigurationSuccessAction());
        
        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }       

    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(saveRAProcessingConfigurationErrorAction());
    }
}

/**
 * Function for saving RA.
 * @param param0
 */
function* handleSaveRemittanceAdviceRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        yield call(post, {
            apiName: API_NAME, 
            path: '/remittanceadvice/save', 
            options: {
                body: payload,
            }
        });
        
        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for save RA invoice.
 * @param param0
 */
function* handleSaveRemittanceAdviceInvoiceRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;
    const payload = {
        ...fields,
    };

    try {
        yield call(post, {
            apiName: API_NAME, 
            path: '/remittanceadvice/save/remittanceadviceinvoices', 
            options: {
                body: payload,
            }
        });        
        
        if (callback) {
            const response = {
                IsSuccess: true,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function responsible for getting the companies the user belongs to from API.
 */
function* handleGetRARebateMappingsRequest({payload: sagaPayload}:any) {
    const { callback } = sagaPayload;
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_RA_REBATE_MAPPINGS
            }
        );

        res.IsSuccess = true;

        if (callback) callback(res);
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }
            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/** 
 * Function for save RA invoice.
 * @param param0
 */
function* handleSaveRemittanceAdviceCreditRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;
    const payload = {
        ...fields,
    };

    try {
        yield call(post, {
            apiName: API_NAME, 
            path: '/remittanceadvice/save/remittanceadvicecredits', 
            options: {
                body: payload,
            }
        });        
        
        if (callback) {
            const response = {
                IsSuccess: true,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for saving RA rebate mappings.
 * @param param0
 */
function* handleSaveRARebateMappingConfigurationRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    
    try {
        yield call(post,
            {
                apiName: API_NAME,
                path: '/remittanceadvice/rebatemappings/save',
                options: {
                    body: payload
                }
            }
        );

        yield put(saveRARebateMappingConfigurationResponseAction());
        
        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(saveRARebateMappingConfigurationResponseAction());
    }
}

/**
 * Function for save RA invoice.
 * @param param0
 */
function* handleSaveRemittanceAdviceRebateRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;
    const payload = {
        ...fields,
    };

    try {
        yield call(post, {
            apiName: API_NAME, 
            path: '/remittanceadvice/save/remittanceadvicerebates', 
            options: {
                body: payload,
            }
        });
        
        if (callback) {
            const response = {
                IsSuccess: true,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: DynamicObject;

            if (typeof err === 'object' && err !== null && 'response' in err) {
                const errorResponse = err as { response: { data: any } };

                returnData = get(errorResponse.response, 'data')
                    ? errorResponse.response.data
                    : { Messages: ['An error occurred'] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

 /** 
 * Function for save RA invoice.
 * @param param0
 */
function* handleGetRAImportStatusRequest({payload: sagaPayload}:any) {
    const {
        RemittanceAdviceId,
        CompanyId,
        callback 
    } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REMITTANCE_ADVICE_IMPORT_STATUS,
                variables: {
                    RemittanceAdviceId: RemittanceAdviceId,
                    CompanyId: CompanyId,
                    callback
                }
            }
        );
        res.IsSuccess = true;

        if (callback) callback(res.data);
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (callback) {
            const returnData = get(err.response, 'data')
                ? err.response.data
                : { Messages: [err.message] };
            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/** Function for reviewing RA.
 * @param param0
 */
function* handleCreateAdjustmentsForInvoicesRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/remittanceadvice/create-adjustments',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                IsSuccess: true,
                cred: res
            };

            callback(response);
        }       

    } catch (err) {
        if (callback) {
            const returnData = get(err.response, 'data')
                ? err.response.data
                : { Messages: ["No adjustments were created."] };
            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function responsible for getting the remittance advice processor report.
 */
function* handleGetRemittanceAdviceProcessorReportRequest({
    payload: { remittanceAdviceId, companyId },
}: any) {
    const errorMessage = `Error fetching remittance advice processor report details. Please try again later.`;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REMITTANCE_ADVICE_PROCESSOR_REPORT,
                variables: {
                    RemittanceAdviceId: remittanceAdviceId,
                    CompanyId: companyId
                }
            }
        );

        const raProcessingConfigurationData = get(
            res.data,
            'GetRemittanceAdviceProcessorReport'
        );

        yield put(getRemittanceAdviceProcessorReportSuccessAction(raProcessingConfigurationData));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getRemittanceAdviceProcessorReportErrorAction([errorMessage]));
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.

function* watchGetRemittanceAdviceRequest() {
    yield takeLatest(
        getRemittanceAdvicesRequestAction.type,
        handleGetRemittanceAdviceRequest
    );
}

function* watchGetRemittanceAdviceDetailsRequest() {
    yield takeLatest(
        getRemittanceAdviceDetails.type,
        handleGetRemittanceAdviceDetailsRequest
    );
}

function* watchGetRemittanceAdviceSumamryRequest() {
    yield takeLatest(
        getRemittanceAdviceSummary.type,
        handleGetRemittanceAdviceSummaryRequest
    );
}

function* watchConfirmRemittanceAdviceRequest() {
    yield takeLatest(
        confirmRemittanceAdviceRequest.type,
        handleConfirmRemittanceAdviceRequest
    );
}

function* watchHandleConfirmRemittanceAdviceDisregardTasksRequest() {
    yield takeLatest(
        confirmRemittanceAdviceDisregardTasksRequest.type,
        handleConfirmRemittanceAdviceDisregardTasksRequest
    );
}

function* watchHandleOrganisationConfirmRemittanceAdviceDisregardTasksRequest() {
    yield takeLatest(
        organisationConfirmRemittanceAdviceDisregardTasksRequest.type,
        handleOrganisationConfirmRemittanceAdviceDisregardTasksRequest
    );
}

function* watchSetRemittanceAdviceSelectedIdRequest() {
    yield takeLatest(
        setRemittanceAdviceSelectedIdRequestAction.type,
        handleSetRemittanceAdviceSelectedIdRequest
    );
}

function* watchRemittanceAdviceAddCommentRequest() {
    yield takeLatest(
        remittanceAdviceAddCommentRequestAction.type,
        handleRemittanceAdviceAddCommentRequest
    );
}

function* watchGetRemittanceAdviceCustomersRequest() {
    yield takeLatest(
        getRemittanceAdviceCustomersRequestAction.type,
        handleGetRemittanceAdviceCustomersRequest
    );
}

function* watchGetRemittanceAdviceInvoicesRequest() {
    yield takeLatest(
        getRemittanceAdviceInvoicesRequestAction.type,
        handleGetRemittanceAdviceInvoicesRequest
    );
}

function* watchGetRemittanceAdviceSalesOrdersRequest() {
    yield takeLatest(
        getRemittanceAdviceSalesOrdersRequestAction.type,
        handleGetRemittanceAdviceSalesOrdersRequest
    );
}

function* watchGetRemittanceAdviceCreditsRequest() {
    yield takeLatest(
        getRemittanceAdviceCreditsRequestAction.type,
        handleGetRemittanceAdviceCreditsRequest
    );
}

function* watchGetRemittanceAdviceRebatesRequest() {
    yield takeLatest(
        getRemittanceAdviceRebatesRequestAction.type,
        handleGetRemittanceAdviceRebatesRequest
    );
}

function* watchGetRemittanceAdviceConversationRequest() {
    yield takeLatest(
        getRemittanceAdviceConversationRequestAction.type,
        handleGetRemittanceAdviceConversationRequest
    );
}

function* watchGetRemittanceAdviceChangesRequest() {
    yield takeLatest(
        getRemittanceAdviceChangesRequestAction.type,
        handleGetRemittanceAdviceChangesRequest
    );
}

function* watchGetRemittanceAdvicePaymentsRequest() {
    yield takeLatest(
        getRemittanceAdvicePaymentsRequestAction.type,
        handleGetRemittanceAdvicePaymentsRequest
    );
}

function* watchGetRACustomFieldsForCompanyRequest() {
    yield takeLatest(
        getRACustomFieldsForCompanyRequestAction.type,
        handleGetRACustomFieldsForCompanyRequest
    );
}

function* watchGetRACustomFieldsForOrganisationRequest() {
    yield takeLatest(
        getRACustomFieldsForOrganisationRequestAction.type,
        handleGetRACustomFieldsForOrganisationRequest
    );
}

function* watchReviewRemittanceAdviceRequest() {
    yield takeLatest(
        reviewRemittanceAdviceRequest.type,
        handleReviewRemittanceAdviceRequest
    );
}

function* watchReviewOrganisationRemittanceAdviceRequest() {
    yield takeLatest(
        reviewOrganisationRemittanceAdviceRequest.type,
        handleReviewOrganisationRemittanceAdviceRequest
    );
}

function* watchGetRAProcessingConfigurationRequest() {
    yield takeLatest(
        getRAProcessingConfigurationRequestAction.type,
        handleGetRAProcessingConfigurationRequest
    );
}

function* watchSaveRAProcessingConfigurationRequest() {
    yield takeLatest(
        saveRAProcessingConfigurationRequestAction.type,
        handleSaveRAProcessingConfigurationRequest
    )
}

function* watchSaveRARebateMappingConfigurationRequest() {
    yield takeLatest(
        saveRARebateMappingConfigurationRequestAction.type,
        handleSaveRARebateMappingConfigurationRequest
    );
}

function* watchGetRARebateMappingsRequest() {
    yield takeLatest(
        getRARebateMappingsRequestAction.type,
        handleGetRARebateMappingsRequest
    );
}

function* watchSaveRemittanceAdviceRequest() {
    yield takeLatest(
        saveRemittanceAdvice.type,
        handleSaveRemittanceAdviceRequest
    );
}

function* watchSaveInvoiceRemittanceAdviceRequest() {
    yield takeLatest(
        saveRemittanceAdviceInvoice.type,
        handleSaveRemittanceAdviceInvoiceRequest
    );
}

function* watchSaveCreditRemittanceAdviceRequest() {
    yield takeLatest(
        saveRemittanceAdviceCredit.type,
        handleSaveRemittanceAdviceCreditRequest
    );
}

function* watchSaveRebateRemittanceAdviceRequest() {
    yield takeLatest(
        saveRemittanceAdviceRebate.type,
        handleSaveRemittanceAdviceRebateRequest
    );
}

function* watchGetRAImportStatusRequest() {
    yield takeLatest(
        getRAImportStatusRequestAction.type,
        handleGetRAImportStatusRequest
    );
}

function* watchCreateAdjustmentsForInvoicesRequest() {
    yield takeLatest(
        createAdjustmentsForInvoicesRequest.type,
        handleCreateAdjustmentsForInvoicesRequest
    );
}

function* watchGetRemittanceAdviceProcessorReportRequest() {
    yield takeLatest(
        getRemittanceAdviceProcessorReportRequest.type,
        handleGetRemittanceAdviceProcessorReportRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* remittanceAdvicesSaga() {
    yield all([
        fork(watchGetRemittanceAdviceRequest),
        fork(watchGetRemittanceAdviceDetailsRequest),
        fork(watchGetRemittanceAdviceSumamryRequest),
        fork(watchConfirmRemittanceAdviceRequest),
        fork(watchHandleConfirmRemittanceAdviceDisregardTasksRequest),
        fork(watchHandleOrganisationConfirmRemittanceAdviceDisregardTasksRequest),
        fork(watchSetRemittanceAdviceSelectedIdRequest),
        fork(watchRemittanceAdviceAddCommentRequest),
        fork(watchGetRemittanceAdviceCustomersRequest),
        fork(watchGetRemittanceAdviceInvoicesRequest),
        fork(watchGetRemittanceAdviceSalesOrdersRequest),
        fork(watchGetRemittanceAdviceCreditsRequest),
        fork(watchGetRemittanceAdviceRebatesRequest),
        fork(watchGetRemittanceAdviceConversationRequest),
        fork(watchGetRemittanceAdviceChangesRequest),
        fork(watchGetRemittanceAdvicePaymentsRequest),
        fork(watchGetRAProcessingConfigurationRequest),
        fork(watchSaveRAProcessingConfigurationRequest),
        fork(watchSaveRARebateMappingConfigurationRequest),
        fork(watchGetRARebateMappingsRequest),
        fork(watchGetRACustomFieldsForCompanyRequest),
        fork(watchGetRACustomFieldsForOrganisationRequest),
        fork(watchReviewRemittanceAdviceRequest),
        fork(watchReviewOrganisationRemittanceAdviceRequest),
        fork(watchSaveRemittanceAdviceRequest),
        fork(watchSaveInvoiceRemittanceAdviceRequest),
        fork(watchSaveCreditRemittanceAdviceRequest),
        fork(watchSaveRebateRemittanceAdviceRequest),
        fork(watchGetRemittanceAdviceProcessorReportRequest),
        fork(watchGetRAImportStatusRequest),
        fork(watchCreateAdjustmentsForInvoicesRequest)
    ]);
}

export default remittanceAdvicesSaga;